import type { SearchProvider, SearchResult } from "../types.ts";
import { MemberAvatar } from "../../../components/avatar/Avatar.tsx";
import Text from "../../../components/text/Text.tsx";
import { isInvite, resolveUserOrInviteName } from "../../../utils/userUtil.ts";
import { Show } from "solid-js";
import type { User } from "../../../state/user.tsx";
import type { Invite } from "../../../state/invite.ts";
import { isValidObjectId } from "../../../utils/objectIdUtil.ts";

export class UserSearchProvider implements SearchProvider {
  readonly type = "USER";

  constructor(
    private readonly usersQuery: (query: { firstName?: string }, size: number) => Promise<(User | Invite)[]>,
    private readonly maxResults: number,
  ) {}

  async search(query: string): Promise<SearchResult[]> {
    // 'username' also matches on 'emailAddress' in backend
    const filters = isValidObjectId(query) ? { id: query } : { username: query, firstName: query, lastName: query };
    const users = await this.usersQuery(filters, this.maxResults);

    return users.map((member) => ({
      type: this.type,
      id: member.id.toString(),
      ContentComponent: () => (
        <div
          class="grid min-w-0 grid-cols-template items-center gap-3 px-3 py-2.5"
          style={{
            "--column-template": "auto minmax(0, 1fr)",
          }}
        >
          <MemberAvatar size="sm" member={member} />
          <div class="flex min-w-0 flex-col">
            <Text
              class="text-base-medium text-primary"
              as="p"
              text={resolveUserOrInviteName(isInvite(member), member)}
              truncate
            />
            <Show when={!isInvite(member)}>
              <Text text={member.emailAddress} as="span" class="text-base-regular text-secondary" truncate />
            </Show>
          </div>
        </div>
      ),
      actions: {
        Enter: {
          type: "navigate",
          href: (customerId) => `/customers/${customerId}/users/${member.id}`,
        },
      },
    }));
  }
}
